import React, { cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '@/context/ThemeContext'

type Props = {
  content: React.ReactNode
  children: React.ReactElement<any>
  delay?: number
  disabled?: boolean
}

// Offset from the cursor, not the trigger element — a tooltip should sit near
// wherever the user is actually looking, which for a wide trigger (e.g. a
// full-width nav row) can be nowhere near the element's own bounding box edge.
const OFFSET_X = 14
const OFFSET_Y = 18
const VIEWPORT_PADDING = 8
const TRANSITION_MS = 120

export const Tooltip: React.FC<Props> = ({ content, children, delay = 300, disabled = false }) => {
  const { currentTheme: t } = useTheme()
  const [rendered, setRendered] = useState(false)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPointer = useRef<{ x: number; y: number } | null>(null)

  const clearTimers = () => {
    if (showTimer.current) { clearTimeout(showTimer.current); showTimer.current = null }
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null }
  }

  const computeFromPoint = useCallback((x: number, y: number) => {
    const tooltipEl = tooltipRef.current
    const tw = tooltipEl?.offsetWidth ?? 0
    const th = tooltipEl?.offsetHeight ?? 0

    let left = x + OFFSET_X
    let top = y + OFFSET_Y
    // Flip to the opposite side of the cursor if there's no room.
    if (left + tw + VIEWPORT_PADDING > window.innerWidth) left = x - tw - OFFSET_X
    if (top + th + VIEWPORT_PADDING > window.innerHeight) top = y - th - OFFSET_Y

    left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - tw - VIEWPORT_PADDING))
    top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - th - VIEWPORT_PADDING))
    setCoords({ top, left })
  }, [])

  // Keyboard focus has no cursor position to anchor to — fall back to just
  // below the trigger's own top-left corner.
  const computeFromTrigger = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) computeFromPoint(rect.left, rect.top + rect.height / 2)
  }, [computeFromPoint])

  const show = () => {
    if (disabled || !content) return
    clearTimers()
    showTimer.current = setTimeout(() => setRendered(true), delay)
  }

  const hide = () => {
    clearTimers()
    setVisible(false)
    hideTimer.current = setTimeout(() => setRendered(false), TRANSITION_MS)
  }

  // Mount hidden first so there's a layout pass to measure against, then flip
  // to visible next frame — this is what makes the fade actually transition.
  useEffect(() => {
    if (!rendered) return
    if (lastPointer.current) computeFromPoint(lastPointer.current.x, lastPointer.current.y)
    else computeFromTrigger()
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [rendered, computeFromPoint, computeFromTrigger])

  useEffect(() => () => clearTimers(), [])

  if (!isValidElement(children)) return children

  const existingRef = (children as any).ref
  const trigger = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node
      if (typeof existingRef === 'function') existingRef(node)
      else if (existingRef && typeof existingRef === 'object') existingRef.current = node
    },
    onMouseEnter: (e: React.MouseEvent) => {
      children.props.onMouseEnter?.(e)
      lastPointer.current = { x: e.clientX, y: e.clientY }
      show()
    },
    onMouseMove: (e: React.MouseEvent) => {
      children.props.onMouseMove?.(e)
      lastPointer.current = { x: e.clientX, y: e.clientY }
      // Keep it glued to the cursor while already showing — otherwise, on a
      // wide trigger, it gets left behind as the mouse keeps moving.
      if (rendered) computeFromPoint(e.clientX, e.clientY)
    },
    onMouseLeave: (e: React.MouseEvent) => {
      children.props.onMouseLeave?.(e)
      lastPointer.current = null
      hide()
    },
    onFocus: (e: React.FocusEvent) => { children.props.onFocus?.(e); show() },
    onBlur: (e: React.FocusEvent) => { children.props.onBlur?.(e); hide() },
  } as any)

  return (
    <>
      {trigger}
      {rendered && content && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          className="fixed z-[9999] pointer-events-none max-w-xs rounded-lg px-2.5 py-1.5 text-xs font-medium leading-snug"
          style={{
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            backgroundColor: t.secondary,
            color: t.text,
            border: `1px solid ${t.border}`,
            boxShadow: t.cardShadow,
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.96)',
            transformOrigin: 'top left',
            transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  )
}

export default React.memo(Tooltip)

// Reusable alongside Tooltip for the common "only show a tooltip when the
// text is actually cut off" pattern — attach the returned ref to the
// truncating element itself (the one with `truncate`/`overflow-hidden`, not
// necessarily the Tooltip's trigger), then pass `disabled={!isTruncated}`.
export function useIsTruncated<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return [ref, isTruncated] as const
}
